import { useEffect, useState, useMemo, useCallback } from "react";
import api from "@/lib/api";
import { useAuth, isLocationUser, homeLocationOf } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { formatApiError, SPEC_KEYS, LOCATIONS } from "@/lib/utils";
import { Blend, ArrowRight, CheckCircle2, Info } from "lucide-react";

const LOC_TABS = ["ALL", ...LOCATIONS];

// ------- Ratio helpers -------
// Given rows: {batch_id, ratio, available} and totalBags,
// return { [batch_id]: bags } — a proportional split of totalBags by ratio,
// clamped to each batch's `available`. Uses largest-remainder rounding.
function distributeByRatio(rows, totalBags) {
  const parts = rows.filter((r) => Number(r.ratio) > 0);
  const sumR = parts.reduce((s, r) => s + Number(r.ratio), 0);
  if (!sumR || !totalBags) return {};

  // First pass: floor allocation
  const raw = parts.map((r) => (Number(r.ratio) / sumR) * totalBags);
  const floors = raw.map((v) => Math.floor(v));
  const remainders = raw.map((v, i) => ({ i, rem: v - floors[i] }));

  let allocated = floors.reduce((s, v) => s + v, 0);
  let leftover = totalBags - allocated;
  // Distribute leftover by largest remainder
  remainders.sort((a, b) => b.rem - a.rem);
  for (const { i } of remainders) {
    if (leftover <= 0) break;
    floors[i] += 1;
    leftover -= 1;
  }

  const out = {};
  parts.forEach((r, idx) => {
    out[r.batch_id] = Math.min(floors[idx], r.available);
  });
  return out;
}

export default function Mixing() {
  const { user } = useAuth();
  const home = homeLocationOf(user);
  const lockedToLocation = isLocationUser(user);

  const [batches, setBatches] = useState([]);
  const [tab, setTab] = useState(lockedToLocation ? home : "ALL");
  const [sel, setSel] = useState({}); // {batch_id: bags}
  const [ratios, setRatios] = useState({}); // {batch_id: ratioValue}
  const [totalRatioBags, setTotalRatioBags] = useState(""); // total bags to distribute by ratio
  const [useRatioMode, setUseRatioMode] = useState(false);
  const [mixNumber, setMixNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  const load = useCallback(async () => {
    const res = await api.get("/batches", { params: { status: "approved" } });
    setBatches(res.data.filter((b) => b.bags_remaining > 0));
  }, []);
  useEffect(() => { load(); }, [load]);

  const visibleBatches = useMemo(
    () => batches.filter((b) => tab === "ALL" ? true : (b.location || "FACTORY") === tab),
    [batches, tab]
  );

  const tabCounts = useMemo(() => {
    const c = { ALL: batches.length };
    for (const l of LOCATIONS) c[l] = batches.filter((b) => (b.location || "FACTORY") === l).length;
    return c;
  }, [batches]);

  const selectedRows = batches.filter((b) => sel[b.id] && Number(sel[b.id]) > 0);

  // Ratio expression (e.g. "2 : 3")
  const ratioExpression = useMemo(() => {
    const parts = selectedRows
      .map((b) => Number(ratios[b.id]) || 0)
      .filter((v) => v > 0);
    return parts.length >= 2 ? parts.join(" : ") : "";
  }, [selectedRows, ratios]);

  const averaged = useMemo(() => {
    const items = selectedRows.map((b) => ({ bags: Number(sel[b.id]) || 0, specs: b.specs || {} }));
    const totalBags = items.reduce((s, i) => s + i.bags, 0);
    const out = {};
    for (const sk of SPEC_KEYS) {
      let ws = 0, w = 0;
      for (const it of items) {
        const v = it.specs[sk.key];
        if (v === null || v === undefined) continue;
        ws += Number(v) * it.bags; w += it.bags;
      }
      out[sk.key] = w > 0 ? Math.round((ws / w) * 1000) / 1000 : null;
    }
    return { specs: out, totalBags };
  }, [sel, selectedRows]);

  // Set bags directly (bags-mode)
  const setBagsFor = (b, val) => {
    const num = parseInt(val, 10);
    const next = { ...sel };
    if (!val || isNaN(num) || num <= 0) delete next[b.id];
    else next[b.id] = Math.min(num, b.bags_remaining);
    setSel(next);
  };

  // Set ratio value for a batch (ratio-mode)
  const setRatioFor = (b, val) => {
    const num = parseFloat(val);
    const nextR = { ...ratios };
    if (!val || isNaN(num) || num <= 0) delete nextR[b.id];
    else nextR[b.id] = num;
    setRatios(nextR);
  };

  // Whenever ratios or total change (in ratio-mode), recompute bags for currently-ratio'd batches
  useEffect(() => {
    if (!useRatioMode) return;
    const tot = parseInt(totalRatioBags, 10);
    if (!tot || tot <= 0) return;
    const rows = batches
      .filter((b) => Number(ratios[b.id]) > 0)
      .map((b) => ({ batch_id: b.id, ratio: Number(ratios[b.id]), available: b.bags_remaining }));
    const dist = distributeByRatio(rows, tot);
    setSel((prev) => {
      const next = { ...prev };
      // clear old ratio-assigned rows first
      for (const b of batches) {
        if (Number(ratios[b.id]) > 0) delete next[b.id];
      }
      for (const [bid, bags] of Object.entries(dist)) {
        if (bags > 0) next[bid] = bags;
      }
      return next;
    });
  }, [ratios, totalRatioBags, useRatioMode, batches]);

  const switchMode = (toRatio) => {
    setUseRatioMode(toRatio);
    setSel({});
    setRatios({});
    setTotalRatioBags("");
  };

  const submit = async () => {
    setErr(""); setLoading(true); setSuccess(null);
    try {
      const items = selectedRows.map((b) => ({
        batch_id: b.id,
        bags: Number(sel[b.id]),
        ...(useRatioMode && ratios[b.id] ? { ratio: Number(ratios[b.id]) } : {}),
      }));
      const payload = {
        mix_number: mixNumber,
        items,
        notes,
        ...(useRatioMode && totalRatioBags ? { total_ratio_bags: Number(totalRatioBags) } : {}),
      };
      const res = await api.post("/mixes", payload);
      setSuccess(res.data);
      setSel({}); setRatios({}); setTotalRatioBags("");
      setMixNumber(""); setNotes("");
      await load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setLoading(false); }
  };

  const totalDistributed = Object.values(sel).reduce((s, v) => s + (Number(v) || 0), 0);
  const targetTotal = useRatioMode ? (parseInt(totalRatioBags, 10) || 0) : 0;
  const distributionOff = useRatioMode && targetTotal > 0 && totalDistributed !== targetTotal;

  return (
    <div className="p-4 sm:p-8">
      <PageHeader
        overline="BLENDING"
        title="Batch Mixing & Averaging"
        description="Combine approved batches into a single blended lot. Specs are weighted-averaged by bag count. Enter bags directly, or switch to ratio mode (e.g. 2 : 3) to split a target total."
      />

      {success && (
        <div className="card p-4 mb-6 flex items-center gap-3" style={{ borderColor: "var(--green)" }} data-testid="mix-success">
          <CheckCircle2 size={20} color="var(--green)" />
          <div>
            <div className="font-medium">Blend created: <span className="font-mono">{success.batch_number}</span></div>
            <div className="text-xs font-mono" style={{ color: "var(--text-muted)" }}>
              {success.bags_count} bags · {success.bags_count * 50} kg
              {success.ratio_expression ? <> · ratio <b>{success.ratio_expression}</b></> : null}
            </div>
          </div>
        </div>
      )}

      {/* Location tabs */}
      <div className="flex gap-1 mb-4 border-b overflow-x-auto" style={{ borderColor: "var(--border)" }}>
        {(lockedToLocation ? [home] : LOC_TABS).map((t) => (
          <button
            key={t}
            className="px-4 py-2 text-sm transition-colors whitespace-nowrap"
            style={{
              color: tab === t ? "var(--text)" : "var(--text-muted)",
              borderBottom: tab === t ? "2px solid var(--orange)" : "2px solid transparent",
              marginBottom: "-1px",
              fontWeight: tab === t ? 600 : 400,
            }}
            onClick={() => { setTab(t); setSel({}); setRatios({}); }}
            data-testid={`mix-tab-${t.toLowerCase()}`}
          >
            {t} <span className="font-mono text-xs" style={{ color: "var(--text-dim)" }}>({tabCounts[t] || 0})</span>
          </button>
        ))}
      </div>

      {/* Mode switcher */}
      <div className="flex flex-wrap gap-3 items-center mb-4">
        <div className="flex gap-1 rounded-md overflow-hidden" style={{ border: "1px solid var(--border)" }}>
          <button
            onClick={() => switchMode(false)}
            className="px-3 py-1.5 text-xs font-mono transition-colors"
            style={{
              background: !useRatioMode ? "var(--orange)" : "transparent",
              color: !useRatioMode ? "#fff" : "var(--text)",
              fontWeight: !useRatioMode ? 600 : 400,
            }}
            data-testid="mix-mode-bags"
          >
            BAGS MODE
          </button>
          <button
            onClick={() => switchMode(true)}
            className="px-3 py-1.5 text-xs font-mono transition-colors"
            style={{
              background: useRatioMode ? "var(--orange)" : "transparent",
              color: useRatioMode ? "#fff" : "var(--text)",
              fontWeight: useRatioMode ? 600 : 400,
              borderLeft: "1px solid var(--border)",
            }}
            data-testid="mix-mode-ratio"
          >
            RATIO MODE
          </button>
        </div>

        {useRatioMode && (
          <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
            <div className="flex items-center gap-2 flex-1 sm:flex-none">
              <label className="text-xs font-mono whitespace-nowrap" style={{ color: "var(--text-muted)" }}>Total bags to blend</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={totalRatioBags}
                onChange={(e) => setTotalRatioBags(e.target.value.replace(/[^0-9]/g, ""))}
                placeholder="e.g. 500"
                className="num-input num-input-lg"
                style={{ width: 160 }}
                data-testid="mix-total-ratio-bags"
              />
            </div>
            {ratioExpression && (
              <div className="text-sm font-mono px-3 py-2 rounded" style={{ background: "var(--bg)", border: "1px solid var(--border)" }} data-testid="mix-ratio-expression">
                RATIO <b style={{ color: "var(--orange)", fontSize: 16 }}>{ratioExpression}</b>
              </div>
            )}
          </div>
        )}
      </div>

      {useRatioMode && (
        <div className="flex items-start gap-2 p-3 mb-4 text-xs" style={{ background: "var(--bg)", border: "1px solid var(--border)", color: "var(--text-muted)" }}>
          <Info size={14} className="mt-0.5" color="var(--orange)" />
          <div>
            Enter a <b>ratio value</b> (e.g. 2 and 3 for a 2 : 3 blend) against each source batch you want to include, then set the <b>total bags</b> to blend. Bags are auto-distributed proportionally. Rounding uses largest-remainder to preserve the total.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card overflow-hidden">
          <div className="px-4 py-3 border-b" style={{ borderColor: "var(--border)" }}>
            <div className="font-heading text-sm font-semibold">
              APPROVED BATCHES @ <span style={{ color: "var(--orange)" }}>{tab}</span> — {useRatioMode ? "Set ratio per source" : "Set bags to use"}
            </div>
          </div>

          {/* Desktop / tablet: table view */}
          <div className="hidden sm:block" style={{ overflowX: "auto", maxHeight: "65vh", overflowY: "auto" }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Batch</th>
                  <th>Location</th>
                  <th>Date</th>
                  <th className="text-right">Available</th>
                  <th className="text-right">Protein</th>
                  <th className="text-right">Moist.</th>
                  {useRatioMode && <th>Ratio</th>}
                  <th>{useRatioMode ? "Bags (auto)" : "Use Bags"}</th>
                </tr>
              </thead>
              <tbody>
                {visibleBatches.length === 0 && (
                  <tr><td colSpan={useRatioMode ? 8 : 7} className="text-center py-8" style={{ color: "var(--text-muted)" }}>No approved batches at {tab}.</td></tr>
                )}
                {visibleBatches.map((b) => (
                  <tr key={b.id} data-testid={`mix-source-${b.batch_number}`}>
                    <td className="font-mono">{b.batch_number}</td>
                    <td className="font-mono text-xs uppercase">{b.location || "FACTORY"}</td>
                    <td className="font-mono">{b.production_date}</td>
                    <td className="font-mono text-right">{b.bags_remaining}</td>
                    <td className="font-mono text-right">{b.specs?.protein ?? "—"}</td>
                    <td className="font-mono text-right">{b.specs?.moisture ?? "—"}</td>
                    {useRatioMode && (
                      <td style={{ width: "110px" }}>
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={ratios[b.id] || ""}
                          onChange={(e) => setRatioFor(b, e.target.value.replace(/[^0-9]/g, ""))}
                          placeholder="0"
                          className="num-input"
                          data-testid={`mix-ratio-${b.batch_number}`}
                        />
                      </td>
                    )}
                    <td style={{ width: "130px" }}>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={sel[b.id] || ""}
                        onChange={(e) => setBagsFor(b, e.target.value.replace(/[^0-9]/g, ""))}
                        readOnly={useRatioMode}
                        placeholder="0"
                        className="num-input"
                        data-testid={`mix-bags-${b.batch_number}`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile: stacked card view */}
          <div className="sm:hidden" style={{ maxHeight: "65vh", overflowY: "auto" }}>
            {visibleBatches.length === 0 && (
              <div className="text-center py-10 text-sm" style={{ color: "var(--text-muted)" }}>No approved batches at {tab}.</div>
            )}
            {visibleBatches.map((b) => {
              const isSelected = Number(sel[b.id]) > 0;
              return (
                <div
                  key={b.id}
                  className="border-b p-4"
                  style={{
                    borderColor: "var(--border)",
                    background: isSelected ? "var(--brand-blue-soft)" : "transparent",
                  }}
                  data-testid={`mix-source-${b.batch_number}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="min-w-0">
                      <div className="font-mono text-base font-semibold truncate" style={{ color: "var(--text)" }}>{b.batch_number}</div>
                      <div className="text-xs font-mono mt-0.5" style={{ color: "var(--text-muted)" }}>
                        {(b.location || "FACTORY")} · {b.production_date}
                      </div>
                    </div>
                    <div className="text-right shrink-0 ml-3">
                      <div className="text-[10px] font-mono uppercase" style={{ color: "var(--text-dim)" }}>Available</div>
                      <div className="font-mono text-lg font-bold" style={{ color: "var(--text)" }}>{b.bags_remaining}</div>
                    </div>
                  </div>

                  <div className="flex gap-3 text-xs font-mono mb-3" style={{ color: "var(--text-muted)" }}>
                    <span>Protein <b style={{ color: "var(--text)" }}>{b.specs?.protein ?? "—"}</b></span>
                    <span>Moist. <b style={{ color: "var(--text)" }}>{b.specs?.moisture ?? "—"}</b></span>
                  </div>

                  <div className={useRatioMode ? "grid grid-cols-2 gap-3" : ""}>
                    {useRatioMode && (
                      <div>
                        <div className="text-[10px] font-mono uppercase mb-1" style={{ color: "var(--text-dim)" }}>Ratio</div>
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={ratios[b.id] || ""}
                          onChange={(e) => setRatioFor(b, e.target.value.replace(/[^0-9]/g, ""))}
                          placeholder="0"
                          className="num-input num-input-lg"
                          data-testid={`mix-ratio-${b.batch_number}`}
                        />
                      </div>
                    )}
                    <div>
                      <div className="text-[10px] font-mono uppercase mb-1" style={{ color: "var(--text-dim)" }}>
                        {useRatioMode ? "Bags (auto)" : "Use bags"}
                      </div>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={sel[b.id] || ""}
                        onChange={(e) => setBagsFor(b, e.target.value.replace(/[^0-9]/g, ""))}
                        readOnly={useRatioMode}
                        placeholder="0"
                        className="num-input num-input-lg"
                        data-testid={`mix-bags-${b.batch_number}`}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="card p-5">
          <div className="text-xs font-mono mb-2" style={{ color: "var(--orange)" }}>// WEIGHTED AVERAGE</div>
          <div className="font-heading text-lg font-semibold mb-4 flex items-center gap-2">
            <Blend size={18} /> Blend Output
          </div>

          <div className="mb-4 p-3" style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
            <div className="text-xs font-mono" style={{ color: "var(--text-muted)" }}>TOTAL BAGS</div>
            <div className="font-heading text-3xl font-bold" data-testid="mix-total-bags">{averaged.totalBags}</div>
            <div className="text-xs font-mono" style={{ color: "var(--text-dim)" }}>
              {averaged.totalBags * 50} KG · {selectedRows.length} SOURCES
              {ratioExpression && <> · <span style={{ color: "var(--orange)" }}>{ratioExpression}</span></>}
            </div>
            {distributionOff && (
              <div className="text-xs font-mono mt-2" style={{ color: "var(--amber)" }} data-testid="mix-distribution-warning">
                ⚠ target {targetTotal}, distributed {totalDistributed} (some sources may be capped by availability)
              </div>
            )}
          </div>

          <div className="space-y-2 mb-5">
            {SPEC_KEYS.map((s) => (
              <div key={s.key} className="flex justify-between items-center text-xs font-mono">
                <span style={{ color: "var(--text-muted)" }}>{s.label}</span>
                <span style={{ color: averaged.specs[s.key] !== null ? "var(--text)" : "var(--text-dim)" }} data-testid={`avg-${s.key}`}>
                  {averaged.specs[s.key] !== null ? `${averaged.specs[s.key]}${s.suffix || ""}` : "—"}
                </span>
              </div>
            ))}
          </div>

          <label>Mix / Lot Number</label>
          <input value={mixNumber} onChange={(e) => setMixNumber(e.target.value)} placeholder="Type your mix number" data-testid="mix-number-input" />

          <label style={{ marginTop: 12 }}>Notes</label>
          <textarea rows="2" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional..." data-testid="mix-notes-input" />

          {err && (
            <div className="text-xs p-2 border mt-3" style={{ color: "var(--red)", borderColor: "var(--red)", background: "rgba(239,68,68,0.1)" }}>
              {err}
            </div>
          )}

          <button
            className="btn btn-primary w-full mt-4"
            disabled={loading || selectedRows.length === 0 || !mixNumber}
            onClick={submit}
            data-testid="create-mix-button"
          >
            <ArrowRight size={16} /> {loading ? "Creating..." : "Create Blend"}
          </button>
        </div>
      </div>
    </div>
  );
}
